import { Fragment, useEffect } from 'react';

import { Link, useNavigation, useParams } from 'react-router';

import { Dialog, Disclosure, Transition } from '@headlessui/react';
import { ChevronRightIcon } from '@heroicons/react/20/solid';
import { XMarkIcon } from '@heroicons/react/24/outline';

import type { Navigation } from '#/brand/navigation';

import type { MegaMenuChildLinks, MegaMenuImageLink, MegaMenuParent } from './main-nav';

function MenuChildLinks({ child }: { child: MegaMenuChildLinks }) {
  return (
    <Disclosure as={'div'}>
      {({ open }) => {
        return (
          <>
            <Disclosure.Button className="flex items-center justify-between text-left">
              <h4 className="">{child.title}</h4>
              <ChevronRightIcon className={open ? 'h-4 w-4 rotate-90 transform' : 'h-4 w-4'} />
            </Disclosure.Button>
            <Disclosure.Panel className="flex flex-col gap-y-4 px-4">
              {child.children?.map((m) => {
                return (
                  <Link to={m.to} key={m.title} className="">
                    <h5>{m.title}</h5>
                  </Link>
                );
              })}
            </Disclosure.Panel>
          </>
        );
      }}
    </Disclosure>
  );
}

function MenuImageLink({ child }: { child: MegaMenuImageLink }) {
  return (
    <Link to={child.to}>
      <h4>{child.title}</h4>
    </Link>
  );
}

function MenuChild({ child }: { child: MegaMenuChildLinks | MegaMenuImageLink }) {
  if (child.type == 'image') {
    return <MenuImageLink child={child} />;
  }

  if (child.type == 'links') {
    return <MenuChildLinks child={child} />;
  }

  return null;
}

function MenuParent({ parent }: { parent: MegaMenuParent }) {
  if (!parent.children) {
    return (
      <Link to={parent.to}>
        <h3 key={parent.title}>{parent.title}</h3>
      </Link>
    );
  }

  return (
    <Disclosure as={'div'}>
      {({ open }) => {
        return (
          <>
            <Disclosure.Button className="flex items-center justify-between text-left">
              <h3>{parent.title}</h3>
              <ChevronRightIcon className={open ? 'h-8 w-8 rotate-90 transform' : 'h-8 w-8'} />
            </Disclosure.Button>
            <Disclosure.Panel className="flex flex-col gap-y-4 px-4 pt-4 text-base">
              {parent.children?.map((m) => {
                return <MenuChild key={m.title} child={m} />;
              })}
            </Disclosure.Panel>
          </>
        );
      }}
    </Disclosure>
  );
}

export default function MobileMenu({
  show,
  setShow,
  navigation,
  menu,
}: {
  show: boolean;
  setShow: (show: boolean) => void;
  navigation: Navigation[];
  menu: MegaMenuParent[];
}) {
  const params = useParams();

  const navigationhook = useNavigation();

  useEffect(() => {
    if (navigationhook.state == 'loading') {
      setShow(false);
    }
  }, [navigationhook, setShow]);

  useEffect(() => {
    if (show) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [show]);

  return (
    <Transition.Root show={show} as={Fragment}>
      <Dialog as="div" className="relative z-40 lg:hidden" onClose={setShow}>
        <Transition.Child
          as={Fragment}
          enter="transition-opacity ease-linear duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition-opacity ease-linear duration-300"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="bg-opacity-25 bg-primary-2/30 fixed inset-0 backdrop-blur-xs" />
        </Transition.Child>
        <div className="fixed inset-0 z-40 flex">
          <Transition.Child
            as={Fragment}
            enter="transition ease-in-out duration-500 transform"
            enterFrom="-translate-x-full"
            enterTo="translate-x-0"
            leave="transition ease-in-out duration-300 transform"
            leaveFrom="translate-x-0"
            leaveTo="-translate-x-full"
          >
            <Dialog.Panel className="bg-primary-2-10 relative flex h-full w-[80%] max-w-sm flex-col overflow-y-auto shadow-xl">
              <button
                type="button"
                className="text-primary-2-80 hover:bg-primary-2-20 absolute top-4 right-4 z-10 rounded-md p-2"
                onClick={() => setShow(false)}
              >
                <span className="sr-only">Close menu</span>
                <XMarkIcon className="h-6 w-6" aria-hidden="true" />
              </button>
              <div className="text-primary-2-80 flex h-full flex-col justify-between gap-6 p-10">
                <div className="flex flex-col gap-y-6 text-lg leading-6 font-medium tracking-tight">
                  {menu.map((m) => {
                    return <MenuParent key={m.title} parent={m} />;
                  })}
                </div>
                <div className="text-medium flex flex-col gap-y-4 leading-4 tracking-tight">
                  {navigation.map((m) => {
                    return (
                      <Link to={m.to(params)} key={m.title}>
                        {m.title != 'kickstarter' && <h4>{m.title}</h4>}
                        {m.title == 'kickstarter' && (
                          <>
                            <img src="/static/kickstarter/icon.svg" alt="kickstarter Icon" />
                          </>
                        )}
                      </Link>
                    );
                  })}
                </div>
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
